import {
  assert,
  hasValue,
  isArray,
  isTrue,
  isNotBlank,
  isElement
} from 'ajax-form-submit/js-utils'

import {
  querySelector,
  hasClass,
  addClass,
  getTargets,
  removeClass,
  registerEvent,
  triggerEvent,
  registerMutationObserver,
  stopDefaultEvent
} from 'ajax-form-submit/js-dom-utils'

import { createDatasetHelper } from 'ajax-form-submit/js-dataset-helper'
import { createProperty } from 'ajax-form-submit/js-property-factory'
import { createInstanceMap } from 'ajax-form-submit/js-cache'

const CLASS_NAME = 'toggle'
const INIT_CLASS_NAME = `${CLASS_NAME}-initialized`
const CLASS_CLASS_NAME = `${CLASS_NAME}-class`
const ATTR_CLASS_NAME = `${CLASS_NAME}-attr`
const LOCK_SCREEN_CLASS_NAME = `${CLASS_NAME}-lock-screen`
const INSIDE_CLASS_NAME = `${CLASS_NAME}-inside`
const OUTSIDE_CLASS_NAME = `${CLASS_NAME}-outside`
const TYPE = ['class', 'attr']

class Toggle {
  static prefix = 'tg'
  static instance = createInstanceMap(
      el => !hasClass(el, INIT_CLASS_NAME),
      el => new Toggle(el))

  #datasetHelper = createDatasetHelper(Toggle.prefix)
  #root
  #groupAttr
  #lockScreen
  #outsideClick

  constructor(el, opt = {}) {
    assert(isElement(el), 1, 'HTMLElement')

    const { keyToAttrName, getValue } = this.#datasetHelper
    const trigger = getValue(el, 'trigger') || 'click'
    const open = isTrue(getValue(el, 'open'))

    this.#root = el
    this.#groupAttr = keyToAttrName('group')
    this.#lockScreen = el.hasAttribute(keyToAttrName('lock-screen'))
    this.#outsideClick = el.hasAttribute(keyToAttrName('outside-click'))
    this.#addDefaultClass()
    this.#registerEvent(trigger)

    open && this.run()
  }

  #addDefaultClass() {
    this.#root.style.cursor = 'pointer'
    this.#root.style.userSelect = 'none'
  }

  #registerEvent(trigger) {
    const { localName, type } = this.#root
    let eventName = trigger
    if (localName === 'select' || type ==='radio')
      eventName = 'select'

    switch(eventName) {
      case 'click':
        this.#handleClick()
        break
      case 'select':
        this.#handleSelect()
        break
      default:
        registerEvent(this.#root, eventName, event => {
          stopDefaultEvent(event)
          this.run()
        })
    }
  }

  #handleClick() {
    const outsideClick = ({ target }) => {
      const toggleTargets = this.#findGroupTargets(this.#root, 'toggle')
      const insideElems = toggleTargets.map(target => target.children).flat()
      insideElems.push(this.#root)
      let isInside = hasClass(target, INSIDE_CLASS_NAME)
        || (!hasClass(target, OUTSIDE_CLASS_NAME) && insideElems.reduce((acc, elem) => acc || elem.contains(target), false))
      if (!isInside) {
        this.#toggle(this.#root, toggleTargets, false)
        document.removeEventListener('click', outsideClick)
      }
    }

    registerEvent(this.#root, 'click', event => {
      this.run()
      this.#outsideClick && document.addEventListener('click', outsideClick, true)
    })
  }

  #handleSelect() {
    const { getValue } = this.#datasetHelper
    registerEvent(this.#root, 'change', event => {
      stopDefaultEvent(event)
      const { options, selectedIndex, checked } = event.target
      let selectEl = options?.[selectedIndex] || event.target
      if (!hasValue(selectEl.value) && !hasValue(checked)) {
        selectEl = this.#root.querySelector('[checked]')
      }

      const value = selectEl.type === 'checkbox' ? isTrue(checked).toString() : selectEl.value
      const toggleTargets = this.#findGroupTargets(this.#root, 'toggle')
      let selectTargets = toggleTargets.filter(({ parent }) => {
        const selectName = this.#datasetHelper.getValue(parent, 'select')
        return isNotBlank(selectName) ? (selectName === value) : true
      })

      this.#toggle(selectEl, toggleTargets, false)
      this.#toggle(selectEl, selectTargets, true)
    })
    triggerEvent(this.#root, 'change')
  }

  #toggle(root, groupTargets, flag) {
    assert(isArray(groupTargets), 1, 'Array')
    if (groupTargets.length < 1)
      return

    if (this.#lockScreen)
      lockScreen(this.#root, flag)

    const { getValue } = this.#datasetHelper
    for (const { parent, children, type, ignore = [] } of groupTargets) {
      for (const el of children) {
        type.includes('class') && toggleClass(el, getToken(getValue(el, 'class')), ignore, flag)
        type.includes('attr') && toggleAttr(root, el, getToken(getValue(el, 'attr')), ignore, flag, getValue)
      }
    }
  }

  #show() {
    this.#toggle(this.#root, this.#findGroupTargets(this.#root, 'show'), true)
  }

  #hide(){
    this.#toggle(this.#root, this.#findGroupTargets(this.#root, 'hide'), false)
  }

  #dismiss() {
    this.#findGroupTargets(this.#root, 'dismiss').forEach(group => group.parent.remove?.())
  }

  #focus() {
    this.#findGroupTargets(this.#root, 'focus')[0]?.parent?.focus?.()
  }

  run() {
    this.#show()
    this.#hide()
    this.#toggle(this.#root, this.#findGroupTargets(this.#root, 'toggle'))
    this.#dismiss()
    this.#focus()
  }

  #findGroupTargets(el, key) {
    const { getValue } = this.#datasetHelper
    let { value, target, type, ...others } = createProperty(getValue(el, key))[0]
    return getTargets(value.concat(target || []), el).map(parent => {
      const groupName = getValue(parent, 'group')
      const isGroup = isNotBlank(groupName)
      const selector = `[${this.#groupAttr}*="${groupName}"]`
      const children = isGroup ? querySelector(selector, parent, true) : [parent]
      return { parent, children, type: type.length > 0 ? type : TYPE, ...others }
    })
  }
}

function getToken(str) {
  const props = createProperty(str)[0]
  return [
    {
      negative: false,
      value: props.value.concat(props.add || [])
    },
    {
      negative: true,
      value: props.remove || []
    }
  ]
}

function toggleClass(el, tokens, ignoreNames, flag) {
  tokens.forEach(({ negative, value }) => value.forEach(name => {
    if (ignoreNames.includes(name))
      return
    const className = `${CLASS_CLASS_NAME}-${name}`
    const isShow = hasValue(flag) ? isTrue(flag) : !hasClass(el, className)
    negative ^ isShow ? addClass(el, name) : removeClass(el, name)
    isShow ? addClass(el, className) : removeClass(el, className)
  }))
}

function toggleAttr(root, el, tokens, ignoreNames, flag, getValue) {
  tokens.forEach(({ negative, value }) => value.forEach(name => {
    if (ignoreNames.includes(name))
      return
    const className = `${ATTR_CLASS_NAME}-${name}`
    const isShow = hasValue(flag) ? isTrue(flag) : !hasClass(el, className)
    const value = generateValue(root, el, name, getValue)[isTrue(negative ^ isShow).toString()]
    value ? el.setAttribute(name, value) : el.removeAttribute(name)
    isShow ? addClass(el, className) : removeClass(el, className)
  }))
}

function generateValue(root, el, name, getValue) {
  const attr = `attr-${name}`
  const rootProp = createProperty(getValue(root, attr))[0]
  const elProp = createProperty(getValue(el, attr))[0]
  return {
    true: getValue(root, `attr-${name}-true`) || rootProp?.true?.[0] || rootProp?.value?.[0]
      || getValue(el, `attr-${name}-true`) || elProp?.true?.[0] || elProp?.value?.[0]
      || true,
    false: getValue(root, `attr-${name}-false`) || rootProp?.false?.[0]
      || getValue(el, `attr-${name}-false`) || elProp?.false?.[0]
      || false
  }
}

function lockScreen(el, flag) {
  const isLockScreen = hasValue(flag) ? isTrue(flag) : !hasClass(parent, LOCK_SCREEN_CLASS_NAME)
  if (isLockScreen) {
    document.documentElement.style.overflow = 'hidden'
    addClass(el, LOCK_SCREEN_CLASS_NAME)
  } else {
    document.documentElement.style.overflow = null
    removeClass(el, LOCK_SCREEN_CLASS_NAME)
  }
}

export default Toggle
window && (window.Toggle = Toggle)

window.addEventListener('DOMContentLoaded', event => {
  const datasetHelper = createDatasetHelper(Toggle.prefix)
  const toggleAttr = datasetHelper.keyToAttrName('toggle')
  const showAttr = datasetHelper.keyToAttrName('show')
  const hideAttr = datasetHelper.keyToAttrName('hide')
  const dismissAttr = datasetHelper.keyToAttrName('dismiss')
  const focusAttr = datasetHelper.keyToAttrName('focus')
  const selector = `[${toggleAttr}],[${showAttr}],[${hideAttr}],[${dismissAttr}],[${focusAttr}]`

  querySelector(selector).forEach(el =>  Toggle.instance.create(el))
  registerMutationObserver(el => {
    if (!isElement(el))
      return

    querySelector(selector, el, true).forEach(el => Toggle.instance.create(el))

    if (el.hasAttribute(toggleAttr)
      || el.hasAttribute(showAttr)
      || el.hasAttribute(hideAttr)
      || el.hasAttribute(dismissAttr)
      || el.hasAttribute(focusAttr))
       Toggle.instance.create(el)
  })
}, { once: true })
